//dependencies
import { format, isValid } from "date-fns";
import React from "react";

// components
import FilterChips from "../../../../components/FilterChips";
import { Filter } from "../../../../components/FilterChips/FilterChips";

import { EmployeeGoalsFilter } from "../Filters";
// css
import css from "./ReadableFilter.module.scss";

const statusLabelMap: Record<string, string> = {
	active: "Active",
	done: "Completed",
	archived: "Archived",
};

const transparencyLabelMap: Record<string, string> = {
	public: "Public",
	private: "Private",
};

const convertFiltersToReadableList = (filters?: Partial<EmployeeGoalsFilter>) => {
	const readableList: Filter[] = [];

	if (filters) {
		Object.keys(filters).forEach((filterKey) => {
			const filter = filters[filterKey];

			if (filterKey === "assignee" && filter && Array.isArray(filter) && filter.length > 0) {
				readableList.push({
					id: "assignee",
					type: "multiple-employees",
					label: "Assignee",
					value: filter.map((assignee) => ({ name: assignee.name, id: assignee.id })),
				} as Filter);
			}

			if (filterKey === "createdDateAfter" && filter && filter instanceof Date && isValid(filter)) {
				readableList.push({
					id: "createdDateAfter",
					type: "string",
					label: "Created After",
					value: format(filter, "MMM d, yyyy"),
				} as Filter);
			}

			if (filterKey === "createdDateBefore" && filter && filter instanceof Date && isValid(filter)) {
				readableList.push({
					id: "createdDateBefore",
					type: "string",
					label: "Created Before",
					value: format(filter, "MMM d, yyyy"),
				} as Filter);
			}

			if (filterKey === "dueDateAfter" && filter && filter instanceof Date && isValid(filter)) {
				readableList.push({
					id: "dueDateAfter",
					type: "string",
					label: "Due After",
					value: format(filter, "MMM d, yyyy"),
				} as Filter);
			}

			if (filterKey === "dueDateBefore" && filter && filter instanceof Date && isValid(filter)) {
				readableList.push({
					id: "dueDateBefore",
					type: "string",
					label: "Due Before",
					value: format(filter, "MMM d, yyyy"),
				} as Filter);
			}

			if (filterKey === "category" && filter && Array.isArray(filter) && filter.length > 0) {
				readableList.push({
					id: "category",
					type: "string",
					label: "Category",
					value: filter.map((category) => category.name),
				} as Filter);
			}

			if (filterKey === "status" && filter && Array.isArray(filter) && filter.length > 0) {
				readableList.push({
					id: "status",
					type: "string",
					label: "Status",
					value: filter.map((status) => statusLabelMap[status] || status),
				} as Filter);
			}

			if (filterKey === "transparency" && filter && Array.isArray(filter) && filter.length > 0) {
				readableList.push({
					id: "transparency",
					type: "string",
					label: "Transparency",
					value: filter.map((transparency) => transparencyLabelMap[transparency] || transparency),
				} as Filter);
			}

			if (filterKey === "outlook" && filter && Array.isArray(filter) && filter.length > 0) {
				readableList.push({
					id: "outlook",
					type: "customRender",
					label: "Outlook",
					value: (
						<div className={css.readableOutlookChip}>
							{filter.map((outlook, index) => (
								<span
									key={`outlook-${outlook.name}-${outlook?.color}-${index}`}
									className={css.outlookEntryContainer}
									data-testid={`outlook-${outlook.name}-${outlook?.color}`}
								>
									<div className={css.outlookColor} style={{ backgroundColor: outlook.color }} />
									<span className={css.outlookName}>
										{outlook.name}
										{index < filter.length - 1 ? "," : ""}
									</span>
								</span>
							))}
						</div>
					),
				} as Filter);
			}

			if (filterKey === "showSharedOnly" && filter && Array.isArray(filter) && filter.length > 0) {
				readableList.push({
					id: "showSharedOnly",
					type: "customRender",
					label: "",
					value: <span>Show Shared Only</span>,
				} as Filter);
			}

			if (filterKey === "unseenUpdatesOnly" && filter && Array.isArray(filter) && filter.length > 0) {
				readableList.push({
					id: "unseenUpdatesOnly",
					type: "customRender",
					label: "",
					value: <span>Goals With New Updates</span>,
				} as Filter);
			}

			if (filterKey === "alignedTo" && filter) {
				readableList.push({
					id: "alignedTo",
					type: "string",
					label: "Aligned To",
					value: filter?.name,
				} as Filter);
			}
		});
	}

	return readableList;
};

type ReadableGoalsFilterChipsProps = {
	filters: Partial<EmployeeGoalsFilter>;
	setFilters: (newFilters: Partial<EmployeeGoalsFilter>) => void;
};

const ReadableGoalsFilterChips = ({ filters, setFilters }: ReadableGoalsFilterChipsProps) => {
	const convertedFilters = convertFiltersToReadableList(filters);

	if (convertedFilters.length === 0) {
		return null;
	}

	return (
		<div>
			<FilterChips
				filters={convertedFilters}
				label="Filters applied: "
				onDeleteFilter={(id) => {
					const currentFilters = { ...filters };
					delete currentFilters[id];

					setFilters({ ...currentFilters });
				}}
			/>
		</div>
	);
};

export default ReadableGoalsFilterChips;
